import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowUp, ChevronDown } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useRef, useState, useEffect, type ReactNode } from "react";
import { MetaSEO } from "@/components/common/MetaSEO";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { getLocaleSwitchPathWithSearch, useLocalizedPublicPath, usePublicLocale } from "@/utils/publicLocale";

export interface LandingFeature {
  icon: ReactNode;
  titleKey: string;
  descKey: string;
}

export interface LandingFaqItem {
  qKey: string;
  aKey: string;
}

export interface LandingPageConfig {
  moduleNumber: string;
  moduleKey: string;
  titleLine1Key: string;
  titleLine2Key: string;
  heroDescKey: string;
  ctaUrl: string;
  ctaLabelKey: string;
  narrativeLabelKey: string;
  narrativeTitleHtml: (t: (key: string) => string) => ReactNode;
  narrativeP1Key: string;
  narrativeP2Key: string;
  features: LandingFeature[];
  solutionCtaLabelKey: string;
  faq?: LandingFaqItem[];
  seo: {
    titleKey: string;
    descriptionKey: string;
    canonical: string;
    schema: object[];
  };
}

export default function LandingPageTemplate({ config }: { config: LandingPageConfig }) {
  const { t, i18n } = useTranslation();
  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { locale } = usePublicLocale();
  const homepagePath = useLocalizedPublicPath("/");
  const manifestoPath = useLocalizedPublicPath("/manifesto");
  const pledgePath = useLocalizedPublicPath("/pledge");

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/^#lp-faq-q-(\d+)$/);
    if (match) {
      const idx = parseInt(match[1], 10);
      setOpenFaq(idx);
      requestAnimationFrame(() => {
        document.getElementById(`lp-faq-q-${idx}`)?.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, []);

  const isDark = mounted ? resolvedTheme === "dark" : true;

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setShowScrollTop(latest > 0.1);
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  const currentYear = new Date().getFullYear();

  const changeLanguage = (lng: string) => {
    void i18n.changeLanguage(lng.toLowerCase());
    navigate(getLocaleSwitchPathWithSearch(location.pathname, location.search, lng.toLowerCase()));
  };

  return (
    <>
      <MetaSEO
        title={t(config.seo.titleKey)}
        description={t(config.seo.descriptionKey)}
        canonical={config.seo.canonical}
        ogType="website"
        schema={config.seo.schema}
      />
      <style>{`
        .landing-solution-map-dark {
          background-image: url("/complexity-map.webp");
          background-image: image-set(
            url("/complexity-map.avif") type("image/avif") 1x,
            url("/complexity-map.webp") type("image/webp") 1x,
            url("/complexity-map.webp") type("image/webp") 1x
          );
        }

        .landing-solution-map-light {
          background-image: url("/complexity_map_white.webp");
          background-image: image-set(
            url("/complexity_map_white.avif") type("image/avif") 1x,
            url("/complexity_map_white.webp") type("image/webp") 1x,
            url("/complexity_map_white.webp") type("image/webp") 1x
          );
        }
      `}</style>
      <div
        ref={containerRef}
        className="min-h-screen grid grid-cols-1 md:grid-cols-[1fr_auto] selection:bg-[#FF3300] selection:text-white transition-colors duration-300"
        style={{
          backgroundColor: "var(--theme-bg, #030303)",
          color: "var(--theme-ink, #ffffff)",
        }}
      >
        {/* Left Column (Main) */}
        <div className="flex flex-col min-w-0 overflow-x-hidden">
          {/* Top Bar */}
          <header
            className="flex justify-between tech-border-b text-[10px] sm:text-xs font-mono uppercase tracking-widest sticky top-0 z-50 transition-colors duration-300 mr-16 md:mr-0"
            style={{
              backgroundColor: "var(--theme-bg, #030303)",
              color: "var(--theme-ink, #ffffff)",
              opacity: 0.5,
            }}
          >
            <div className="p-4 tech-border-r flex items-center gap-3">
              <Link
                to={homepagePath}
                className="flex items-center gap-2 hover:opacity-100 transition-all group"
                style={{ color: "var(--theme-ink)" }}
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-bold font-display">BABETE</span>
              </Link>
            </div>
            <div className="p-4 tech-border-l flex items-center justify-center">
              <span
                className="w-2 h-2 rounded-full animate-pulse mr-2"
                style={{ backgroundColor: "var(--theme-accent, #FF3300)" }}
              ></span>
              STATUS: ONLINE
            </div>
          </header>

          <main className="flex-1 flex flex-col" role="main">
            {/* Hero Section */}
            <section className="relative tech-border-b flex flex-col justify-center min-h-[50vh] md:min-h-[70vh] pt-16 pb-10 px-5 pr-24 md:p-12 lg:p-24 overflow-hidden">
              <div
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  backgroundImage: "radial-gradient(circle at 2px 2px, var(--theme-dot) 1px, transparent 0)",
                  backgroundSize: "40px 40px",
                }}
              ></div>

              <motion.div
                style={{ y: y1, opacity: heroOpacity, backgroundColor: "rgba(255, 51, 0, 0.05)" }}
                className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none"
                aria-hidden="true"
              ></motion.div>

              <div className="relative z-10 w-full max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
                <motion.div
                  className="lg:col-span-8 flex flex-col justify-center"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div
                    className="font-mono text-sm mb-6 tracking-widest uppercase flex items-center gap-4"
                    style={{ color: "var(--theme-accent, #FF3300)" }}
                  >
                    <span>{t(config.moduleKey)}</span>
                    <span className="w-12 h-px" style={{ backgroundColor: "var(--theme-accent, #FF3300)", opacity: 0.5 }}></span>
                  </div>
                  <h1 className="font-display text-[12vw] md:text-[8vw] lg:text-[120px] leading-[0.85] tracking-tighter uppercase mb-6 md:mb-12">
                    {t(config.titleLine1Key)}
                    <br />
                    <span className="opacity-30 hover:opacity-100 transition-opacity duration-700">
                      {t(config.titleLine2Key)}
                    </span>
                  </h1>
                  <p className="text-xl md:text-3xl font-sans font-light leading-relaxed max-w-2xl" style={{ opacity: 0.7 }}>
                    {t(config.heroDescKey)}
                  </p>
                  <p className="font-mono text-[10px] md:text-xs mt-6 md:mt-8 tracking-widest uppercase" style={{ opacity: 0.4 }}>
                    {t("trust.statement")}
                  </p>
                </motion.div>

                <motion.div
                  className="lg:col-span-4 flex flex-col justify-end lg:items-end lg:text-right"
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                >
                  <a
                    href={config.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex items-center justify-center gap-4 px-8 py-6 text-lg font-mono uppercase tracking-widest transition-all duration-500 overflow-hidden"
                    style={{
                      backgroundColor: "var(--theme-ink)",
                      color: "var(--theme-bg)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "var(--theme-accent, #FF3300)";
                      e.currentTarget.style.color = "#ffffff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "var(--theme-ink)";
                      e.currentTarget.style.color = "var(--theme-bg)";
                    }}
                  >
                    <span className="relative z-10">{t(config.ctaLabelKey)}</span>
                    <ArrowUpRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
                  </a>
                </motion.div>
              </div>
            </section>

            {/* Narrative Section 1 */}
            <section className="grid grid-cols-1 lg:grid-cols-2 tech-border-b">
              <div
                className="p-8 pr-24 md:p-16 lg:p-24 tech-border-r flex flex-col justify-center"
                style={{ backgroundColor: "var(--theme-surface, #111111)" }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                >
                  <div
                    className="font-mono text-xs mb-8 tracking-widest uppercase"
                    style={{ color: "var(--theme-accent, #FF3300)" }}
                  >
                    {t(config.narrativeLabelKey)}
                  </div>
                  <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter mb-8 leading-none">
                    {config.narrativeTitleHtml(t)}
                  </h2>
                </motion.div>
              </div>

              <div className="p-8 pr-24 md:p-16 lg:p-24 flex flex-col justify-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <p className="text-lg md:text-2xl font-sans font-light leading-relaxed mb-8" style={{ opacity: 0.7 }}>
                    {t(config.narrativeP1Key)}
                  </p>
                  <p className="text-lg md:text-2xl font-sans font-light leading-relaxed" style={{ opacity: 0.7 }}>
                    {t(config.narrativeP2Key)}
                  </p>
                </motion.div>
              </div>
            </section>

            {/* Features Grid */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 tech-border-b">
              {config.features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`p-8 pr-24 md:p-12 transition-colors duration-500 ${i !== 3 ? "tech-border-r" : ""} ${i < 2 ? "tech-border-b lg:border-b-0" : ""} ${i === 2 ? "tech-border-b md:border-b-0" : ""}`}
                  style={{ cursor: "default" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--theme-surface, #111111)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {feature.icon}
                  <h3 className="font-display text-2xl uppercase tracking-tighter mb-4">{t(feature.titleKey)}</h3>
                  <p className="font-sans font-light text-sm leading-relaxed" style={{ opacity: 0.6 }}>
                    {t(feature.descKey)}
                  </p>
                </motion.div>
              ))}
            </section>

            {/* FAQ Section */}
            {config.faq && config.faq.length > 0 && (
              <section className="tech-border-b py-12 md:py-20 px-5 pr-24 md:px-12 lg:px-24" aria-labelledby="lp-faq-heading">
                <div className="max-w-[900px] mx-auto">
                  <h2
                    id="lp-faq-heading"
                    className="font-display text-2xl md:text-4xl uppercase tracking-tighter mb-8 md:mb-12"
                  >
                    {t("faq.title")}
                  </h2>
                  <div className="flex flex-col">
                    {config.faq.map((item, i) => (
                      <div key={i} className="tech-border-b last:border-b-0">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full flex items-center justify-between py-5 md:py-6 text-left !min-w-0 !min-h-0 group"
                          aria-expanded={openFaq === i}
                          aria-controls={`lp-faq-answer-${i}`}
                        >
                          <span id={`lp-faq-q-${i}`} className="font-sans text-sm md:text-base font-medium pr-4 group-hover:opacity-100 transition-opacity opacity-80">
                            {t(item.qKey)}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 shrink-0 transition-transform duration-300 opacity-50 ${openFaq === i ? "rotate-180" : ""}`}
                          />
                        </button>
                        <div
                          id={`lp-faq-answer-${i}`}
                          role="region"
                          aria-labelledby={`lp-faq-q-${i}`}
                          className={`overflow-hidden transition-all duration-300 ${openFaq === i ? "max-h-[1000px] pb-5 md:pb-6" : "max-h-0"}`}
                        >
                          <p
                            className="font-sans text-sm md:text-base leading-relaxed opacity-60 [&_a]:underline [&_a]:underline-offset-2 [&_a]:opacity-80 [&_a:hover]:opacity-100 [&_a]:transition-opacity [&_strong]:font-semibold [&_strong]:opacity-90"
                            dangerouslySetInnerHTML={{ __html: t(item.aKey) }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Narrative Section 2: Parallax Solution */}
            <section
              className="relative min-h-[80vh] flex items-center justify-center p-6 pr-24 md:p-12 tech-border-b overflow-hidden transition-colors duration-500"
              style={{
                backgroundColor: isDark ? "#050505" : "var(--theme-bg)",
                color: isDark ? "#ffffff" : "var(--theme-ink)",
              }}
            >
              <motion.div
                style={{
                  y: y2,
                }}
                className={`absolute inset-0 md:inset-[-20%] bg-[length:250%_auto] sm:bg-[length:150%_auto] md:bg-cover bg-center bg-no-repeat pointer-events-none ${isDark ? "landing-solution-map-dark opacity-50" : "landing-solution-map-light opacity-30"}`}
              ></motion.div>

              {/* Gradient overlays */}
              <div
                className="absolute inset-0 pointer-events-none opacity-90 transition-colors duration-500"
                style={{
                  background: isDark
                    ? "linear-gradient(to bottom, #050505, transparent, #050505)"
                    : "linear-gradient(to bottom, var(--theme-bg), transparent, var(--theme-bg))",
                }}
              ></div>
              <div
                className="absolute inset-0 pointer-events-none opacity-50 transition-colors duration-500"
                style={{
                  background: isDark
                    ? "linear-gradient(to right, #050505, transparent, #050505)"
                    : "linear-gradient(to right, var(--theme-bg), transparent, var(--theme-bg))",
                }}
              ></div>

              <div className="relative z-10 max-w-4xl mx-auto text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                >
                  <h2 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase tracking-tighter mb-8 leading-none">
                    {t("landing.shared.solution_title_1")}
                    <br />
                    {t("landing.shared.solution_title_2")}
                  </h2>
                  <p className="text-xl md:text-3xl font-sans font-light leading-relaxed mb-12 max-w-2xl mx-auto" style={{ opacity: 0.6 }}>
                    {t("landing.shared.solution_subtitle")}
                  </p>

                  <a
                    href={config.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-4 px-8 py-4 text-sm font-mono uppercase tracking-widest transition-all duration-500"
                    style={{
                      backgroundColor: isDark ? "#ffffff" : "var(--theme-ink)",
                      color: isDark ? "#000000" : "var(--theme-bg)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "var(--theme-accent, #FF3300)";
                      e.currentTarget.style.color = "#ffffff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isDark ? "#ffffff" : "var(--theme-ink)";
                      e.currentTarget.style.color = isDark ? "#000000" : "var(--theme-bg)";
                    }}
                  >
                    <span>{t(config.solutionCtaLabelKey)}</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </motion.div>
              </div>
            </section>
          </main>

          {/* Footer */}
          <footer className="p-6 pr-24 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-8 font-mono text-[9px] md:text-[10px] opacity-40 uppercase tracking-[0.2em] leading-loose">
            <div className="flex flex-col gap-1">
              <div>{t("footer.rights", { year: currentYear })}</div>
              <div>{t("footer.clarity")}</div>
            </div>
            <div className="flex flex-col md:items-end gap-1">
              <Link to={manifestoPath} className="hover:opacity-100 transition-opacity">
                {t("footer.manifesto")}
              </Link>
              <Link to={pledgePath} className="hover:opacity-100 transition-opacity">
                {t("footer.pledge")}
              </Link>
            </div>
          </footer>
        </div>

        {/* Right Column (Sidebar) */}
        <aside
          className="fixed right-0 top-0 h-screen w-16 md:w-24 md:sticky tech-border-l flex flex-col backdrop-blur-md md:backdrop-blur-none z-50 transition-colors duration-300"
          style={{
            backgroundColor: mounted ? (isDark ? "rgba(3,3,3,0.5)" : "rgba(244,244,240,0.5)") : "rgba(3,3,3,0.5)",
          }}
        >
          <div className="p-4 tech-border-b flex items-center justify-center text-[10px] sm:text-xs font-mono uppercase tracking-widest">
            <button
              onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
              className="transition-opacity whitespace-nowrap opacity-50 hover:opacity-100 !min-w-0 !min-h-0"
            >
              {mounted ? (isDark ? "LIGHT" : "DARK") : "DARK"}
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center py-6 gap-8 text-[10px] sm:text-xs font-mono uppercase tracking-widest">
            {["PT", "EN", "FR", "ES"].map((l) => (
              <button
                key={l}
                onClick={() => changeLanguage(l)}
                className={`transition-all duration-300 !min-w-0 !min-h-0 ${
                  locale === l.toLowerCase()
                    ? "font-bold opacity-100"
                    : "opacity-40 hover:opacity-100"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          {/* Scroll to Top Button */}
          <div className="relative p-4 tech-border-t flex items-center justify-center mt-auto w-full overflow-hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showScrollTop ? 1 : 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: "radial-gradient(circle at 1px 1px, var(--theme-dot) 1px, transparent 0)",
                backgroundSize: "16px 16px",
              }}
            />
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: showScrollTop ? 1 : 0, scale: showScrollTop ? 1 : 0.8 }}
              transition={{ duration: 0.3 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className={`relative z-10 w-10 h-10 md:w-12 md:h-12 rounded-full tech-border flex items-center justify-center hover:text-[#FF3300] transition-all duration-300 !min-w-0 !min-h-0 ${
                showScrollTop ? "pointer-events-auto" : "pointer-events-none"
              }`}
              aria-label="Scroll to top"
            >
              <ArrowUp className="w-4 h-4 md:w-5 md:h-5" />
            </motion.button>
          </div>
        </aside>
      </div>
    </>
  );
}
